/**
 * Cart upsell ("You'll love these").
 *
 * Shows a list of product suggestions inside the cart drawer, based on what is
 * in the cart. Cards come from Shopify's product recommendations (rendered by
 * sections/cart-upsell.liquid). Clicking ADD puts the chosen variant in the cart
 * and refreshes the drawer.
 *
 * Shopify returns at most 10 products per request, so we ask for the first few
 * cart items and, when that gives fewer than WANTED_CARDS suggestions, also for
 * products related to the first suggestions. At most MAX_CARDS are shown.
 */

const PROTECTION_HANDLE = 'shipping-protection';
const MAX_SEEDS = 3;
const PER_REQUEST = 10;
const WANTED_CARDS = 14;
const MAX_CARDS = 20;
const REFRESH_DELAY = 450;

/** @type {null | ((sectionId: string, html: string, options?: object) => unknown)} */
let morphSection = null;

import('@theme/section-renderer')
  .then((module) => {
    morphSection = module.morphSection;
  })
  .catch((error) => {
    console.warn('[cart-upsell] section-renderer not available:', error);
  });

function routeRoot() {
  const shopify = window.Shopify;
  return shopify && shopify.routes && shopify.routes.root ? shopify.routes.root : '/';
}

class CartUpsell extends HTMLElement {
  #listHtml = '';
  #loading = false;
  #again = false;
  #timer = 0;
  #observer = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener('cart:updated', this.#onCartChange);

    // If the theme re-renders the drawer and wipes our cards, put them back.
    this.#observer = new MutationObserver(() => this.#restoreIfWiped());
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden'],
    });

    this.refresh();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('cart:updated', this.#onCartChange);
    if (this.#observer) this.#observer.disconnect();
    window.clearTimeout(this.#timer);
  }

  /**
   * Loads the suggestion cards Shopify has for one product.
   * @returns {Promise<{id: string | null, html: string}[]>}
   */
  async #loadCards(root, sectionId, productId) {
    try {
      const response = await fetch(
        `${root}recommendations/products?section_id=${encodeURIComponent(sectionId)}&product_id=${productId}&limit=${PER_REQUEST}&intent=related`,
        { credentials: 'same-origin' }
      );
      if (!response.ok) return [];

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

      return Array.from(doc.querySelectorAll('.cart-upsell__card')).map((card) => ({
        id: card.getAttribute('data-product-id'),
        html: card.outerHTML,
      }));
    } catch (error) {
      return [];
    }
  }

  async refresh() {
    if (this.#loading) {
      this.#again = true;
      return;
    }

    this.#loading = true;

    try {
      const root = routeRoot();
      const cartResponse = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();

      const realItems = cart.items.filter((item) => item.handle !== PROTECTION_HANDLE);

      if (!realItems.length) {
        this.#clear();
        return;
      }

      const inCart = new Set(cart.items.map((item) => String(item.product_id)));
      const sectionId = this.dataset.sectionId || 'cart-upsell';
      const seeds = realItems.slice(0, MAX_SEEDS).map((item) => item.product_id);

      const found = [];
      const seen = new Set();

      const collect = (list) => {
        for (const entry of list) {
          if (found.length >= MAX_CARDS) return;
          if (!entry.id || seen.has(entry.id) || inCart.has(entry.id)) continue;
          seen.add(entry.id);
          found.push(entry);
        }
      };

      const first = await Promise.all(seeds.map((id) => this.#loadCards(root, sectionId, id)));
      first.forEach(collect);

      // Too few? Also ask for products related to the first suggestions.
      if (found.length > 0 && found.length < WANTED_CARDS) {
        const extraSeeds = found.slice(0, MAX_SEEDS).map((entry) => entry.id);
        const more = await Promise.all(extraSeeds.map((id) => this.#loadCards(root, sectionId, id)));
        more.forEach(collect);
      }

      if (!found.length) {
        this.#clear();
        return;
      }

      this.#show(
        `<div class="cart-upsell__list" data-cart-upsell-list>${found.map((entry) => entry.html).join('')}</div>`
      );
    } catch (error) {
      console.warn('[cart-upsell] Could not load suggestions:', error);
    } finally {
      this.#loading = false;

      if (this.#again) {
        this.#again = false;
        this.refresh();
      }
    }
  }

  #show(html) {
    const body = this.querySelector('.cart-upsell__body');
    if (!body) return;

    this.#listHtml = html;
    body.innerHTML = html;
    this.hidden = false;
  }

  #clear() {
    const body = this.querySelector('.cart-upsell__body');

    this.#listHtml = '';
    if (body) body.innerHTML = '';
    this.hidden = true;
  }

  #restoreIfWiped() {
    if (!this.#listHtml) return;

    const body = this.querySelector('.cart-upsell__body');
    if (!body) return;

    if (!body.querySelector('[data-cart-upsell-list]')) {
      body.innerHTML = this.#listHtml;
    }

    if (this.hidden) this.hidden = false;
  }

  #syncHtml() {
    const list = this.querySelector('[data-cart-upsell-list]');

    if (list && list.querySelector('.cart-upsell__card')) {
      this.#listHtml = list.outerHTML;
    } else {
      this.#clear();
    }
  }

  #onCartChange = () => {
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.refresh(), REFRESH_DELAY);
  };

  #onClick = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-cart-upsell-add]');
    if (!button || button.disabled) return;

    const card = button.closest('.cart-upsell__card');
    const field = card ? card.querySelector('[data-cart-upsell-variant]') : null;
    const variantId = field ? Number(field.value) : 0;
    if (!variantId) return;

    const cartSectionId = this.dataset.cartSectionId || 'cart-drawer-section';
    const label = button.textContent;

    button.disabled = true;
    button.textContent = '...';

    try {
      const response = await fetch(`${routeRoot()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: cartSectionId,
          sections_url: window.location.pathname,
        }),
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Could not add this item');
      }

      const sectionHtml = result.sections ? result.sections[cartSectionId] : null;

      if (sectionHtml && morphSection) {
        await morphSection(cartSectionId, sectionHtml, { mode: 'hydration' });
      } else {
        window.location.reload();
        return;
      }

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: {} }));

      if (card) card.remove();
      this.#syncHtml();
    } catch (error) {
      console.warn('[cart-upsell]', error);
      button.textContent = 'Not available';

      window.setTimeout(() => {
        button.textContent = label;
        button.disabled = false;
      }, 2000);

      return;
    }

    button.disabled = false;
    button.textContent = label;
  };
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
